import Enumerable from 'linq';
import { LameFlooringLayout } from 'Plugins/FloorPlan/Components/Laizes/Laize';
import { IMapItem, YesOrNo } from 'Plugins/FloorPlan/Models/IMapItem';
import { QuestionContextUtils } from 'Web/Pages/Quote/Utils/QuestionContextUtils';

import { I18n } from '../Locales/I18nService';
import { AttributDto, ProduitDto } from '../Models/Questions/ProduitDto';
import { QuestionItem, QuestionStep } from '../Models/Questions/QuestionDto';
import { LocalityDto } from '../Models/TravelExpenses/TravelExpenseDto';
import { RoomType } from '../Plugins/FloorPlan/Models/IRoom';
import { ObjectUtils } from './ObjectUtils';
import { QuestionsHelper } from './QuestionsHelper';

export type EvaluationContext = Record<string, any>;
export interface ContournementsContext {
    //* SOL
    pipe?: number;
    mounted_element?: number;
    mounted_radiator?: number;
    pillar?: number;
    stonewall?: number;
    stairs?: number;
    railing?: number;
    tree?: number;
    //* WALL
    wall_pipe?: number;
    wall_pipe_hole?: number;
    electric_equipment?: number;
    wall_pillar?: number;
    hotspot?: number;
}

export interface StairsStepsContext {
    //* Stairs - Marches Specifics
    stairs_steps_shape: any;
    stairs_steps_length_marches: any;
    stairs_steps_length_giron: any;
    stairs_steps_length_contremarches: any;

    stairs_steps_total_marches_droites: any;
    stairs_steps_total_marches_tournantes: any;
    stairs_steps_total_marches_arrondies: any;
    stairs_steps_total_paliers: any;
}

export interface QuestionContext {
    //* in evaluations : { '$name1' : $value1,  '$name2' : $value2 }
    evaluations?: EvaluationContext;

    km_over_20?: number;

    //* Surface totale
    total_area?: number;

    //* COMMON ROOM - WALL
    revetement_type?: RoomType;
    contournements?: ContournementsContext;

    //* Pièce contiguës et présence de longueur du raccord
    are_rooms_contiguous?: boolean;
    connection_needed?: boolean;

    //* ROOM

    room_type?: any;
    room_heating_type?: any;
    room_floor?: any;
    room_existing_floor_type?: any;
    has_room_existing_flooring?: any;
    room_should_lay_plinthes_inside_room_element?: any;
    room_length?: any;
    room_surface?: any;
    localisation_piece?: any;

    //* WALL
    room_wall?: any;
    wall_type?: any;
    wall_floor?: any;
    has_wall_existing_flooring?: any;
    wall_existing_floor_type?: any;

    wall_length?: number;
    wall_surface?: number;
    wall_max_height?: number;

    //* Stairs Specifics
    stairs_floor?: any;
    has_stairs_existing_flooring?: any;
    stairs_existing_floor_type?: any;

    stairs_total_marches_droites?: number;
    stairs_total_marches_tournantes?: number;
    stairs_total_marches_arrondies?: number;
    stairs_total_paliers?: number;
    stairs_total_marches?: number;
    stairs_total_contremarches?: number;
    stairs_has_contremarches?: YesOrNo;
    stairs_incompatible_with_passage?: YesOrNo;

    stairs_steps?: StairsStepsContext[];

    //* Questions Floorplans, opening
    opening_type_localized?: any;
    opening_dimension_centimetre?: number;

    //* Questions Floorplans, roomElement
    room_element_type_localized?: any;
    should_lay_flooring_inside?: string;
    should_lay_plinthes_inside?: string;
    room_element_area?: number;

    flooring_direction?: any;

    rooms?: QuestionContext[];
    walls?: QuestionContext[];
    stairs?: QuestionContext[];

    openings?: QuestionContext[];
    room_items?: QuestionContext[];

    prix_metier_de_pose?: string;

    [field: string]: any;
}

export class QuestionContextHelper {
    //* To identify optional questions without value
    public static NOT_SET = 'NOT_SET';

    public static toQuestionDebug = (id: string): string => `question_debug_${id}`;

    //* question type SingleChoice
    public static toQuestionOption = (id: string): string => `question_${id}_option`;
    public static toQuestionValue = (id: string): string => `question_${id}_value`;
    public static toOptionId = (id: string): string => `option_${id}`;

    //* question type Comment
    public static toQuestionCommentvalue = (id: string): string => `question_${id}_value`;

    //* question type Produit
    //* public static toQuestion = (id: string): string => `question_${id}`; //
    //!: {
    //!     attribut_produit_F001-ChauffageSol : F001-ChauffageSol,
    //!     attribut_produit_F001-Decor : F001-Decor
    //!: }
    public static toQuestionProduitValue = (id: string) => `question_${id}_produit`; //* NOT USED
    public static toQuestionProduitKey = (id: string) => `question_${id}`;
    public static toProduitAttributName = (name: string) => `attribut_produit_${name}`;
    public static infoProduitKey = 'infos_produit';

    public static toQuestionProduitValues = (id: string) => `question_${id}_produits`; //PANIER

    public static toProduitValue = (produit: ProduitDto) => {
        const produitContext: QuestionContext = {};
        const attributs: Array<AttributDto> = produit.attributs ?? [];
        attributs.forEach((attribut: AttributDto) => {
            produitContext[QuestionContextHelper.toProduitAttributName(attribut.name)] = attribut.value;
        });
        produitContext[QuestionContextHelper.infoProduitKey] = produit.infos_produit;

        return produitContext;
    };

    public static toZipCodeValue = (localityDto: LocalityDto) => {
        const zipCodeContext: QuestionContext = {};
        if (localityDto.travel_expenses) {
            zipCodeContext.km_over_20 = localityDto.travel_expenses.billable_distance_in_meters / 1000;
        }
        return zipCodeContext;
    };

    public static withRegionParisienne = (localityDto: LocalityDto, context: QuestionContext = {}): QuestionContext => {
        const evaluationsContext = context.evaluations ?? {};
        evaluationsContext[QuestionContextHelper.toRegionParisienne] = localityDto.travel_expenses?.is_paris_area
            ? QuestionContextHelper.toIsRegionPariennes
            : undefined;
        context.evaluations = evaluationsContext;
        return context;
    };

    public static toEvaluationValue = (key: string): string => `${key}`;

    public static toRegionParisienne = 'région parisienne';
    public static toIsRegionPariennes = 'oui';

    //ragréage obligatoire
    public static ragreageObligatoreKey = 'Ragréage';
    public static ragreageObligatoreValue = 'Obligatoire';

    public static transformIntoLongFloorName(floorName: string = '') {
        return floorName === I18n.get('RoomFloorList_RDC')
            ? I18n.get('RoomFloorList_RDC_Long')
            : floorName;
    }

    public static toWallFlooringDirection = (lameFlooringLayout?: LameFlooringLayout): string => {
        if (!lameFlooringLayout) {
            return QuestionContextHelper.NOT_SET;
        }
        //* Voir le BO pour trouvers ces valeurs
        return lameFlooringLayout === LameFlooringLayout.LamePleine
            ? 'entier'
            : 'optimisé';
    }

    public static isRagreageObligatoireExist(rooms: IMapItem[] = []): boolean {
        if (!rooms.length) {
            return false;
        }
        const ragreageObligatoireIsInEvaluation = (evaluationContext: EvaluationContext = {}) => {
            return (
                evaluationContext[QuestionContextHelper.ragreageObligatoreKey] ===
                QuestionContextHelper.ragreageObligatoreValue
            );
        };
        const roomWithRagreage = Enumerable.from(rooms).firstOrDefault((x) => {
            // const roomQuestions = x.questions.filter((x: QuestionItem) => x.showQuestion);
            // const findQuestion = roomQuestions.find((x: QuestionItem) => ragreageObligatoireIsInEvaluation(x.context.evaluations));
            // hasRagreageObligatoire = Boolean(findQuestion);
            const lastContext = QuestionContextUtils.getLastStepContext(x, QuestionStep.StepFinitions);
            return ragreageObligatoireIsInEvaluation(lastContext.evaluations);
        });
        return Boolean(roomWithRagreage);
    }

    public static mergeContext = (
        previousContext: QuestionContext = {},
        context: QuestionContext = {}
    ): QuestionContext => {
        context.evaluations = QuestionContextHelper.mergeEvaluations(previousContext.evaluations, context.evaluations);
        return {
            ...ObjectUtils.clone(previousContext),
            ...ObjectUtils.clone(context),
        };
    };

    public static mergeEvaluations = (
        previousEvaluationContext: EvaluationContext = {},
        evaluationContext: EvaluationContext = {}
    ): QuestionContext => {
        const mergeEvaluationContext: EvaluationContext = {
            ...ObjectUtils.clone(previousEvaluationContext ?? {}),
            ...ObjectUtils.clone(evaluationContext ?? {}),
        };
        return ObjectUtils.clone(mergeEvaluationContext);
    };

    //* INv3
    //* last question habitation + question produit principal
    public static getBaseQuestionsContext = (
        questions: QuestionItem[] = [],
        questionProduitPrincipal?: QuestionItem
    ): QuestionContext => {
        const lastQuestionContext = questions[questions.length - 1]?.context;
        if (questionProduitPrincipal) {
            const valueContext = QuestionsHelper.getValidatedContext(questionProduitPrincipal);
            return QuestionContextHelper.mergeContext(lastQuestionContext, valueContext);
        }
        return QuestionContextHelper.mergeContext(lastQuestionContext, {});
    };
}
